import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { loginUser, getCaptcha } from "../api"; 
import { navConfig } from "../App"; 
import iconqr from "../assets/qr.png"; 

const TEMPLATES = [
  "I confirm that all information provided is true and accurate.",
  "I will not share my account credentials with others.",
  "Vouchers are non-transferable and valid only for the stated period.",
  "I agree to receive notifications related to voucher usage and offers.",
  "Misuse or fraudulent activity may result in account suspension.",
  "I consent to collection of usage data to improve services.",
  "I acknowledge terms may change and agree to review updates.",
  "I will follow all rules when redeeming vouchers and offers.",
  "I agree not to attempt reverse-engineering or tampering with this app.",
  "I accept the app's privacy policy and refund/cancellation policy."
];

const LoginPage = () => {
  const navigate = useNavigate();
  const formRef = useRef(null);

  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [currentTerms, setCurrentTerms] = useState([]);

  // Captcha State
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaImage, setCaptchaImage] = useState(null); 
  const [captchaId, setCaptchaId] = useState(null);       
  const [captchaValue, setCaptchaValue] = useState("");   
  const [errorMsg, setErrorMsg] = useState("");

  const pickFiveRandom = () => {
    const copy = [...TEMPLATES];
    const result = [];
    for (let i = 0; i < 10 && copy.length > 0; i++) {
      const idx = Math.floor(Math.random() * copy.length);
      result.push(copy.splice(idx, 1)[0]);
    }
    return result;
  };

  const fetchCaptcha = async () => {
    try {
      const response = await getCaptcha();
      setCaptchaId(response.data.id);
      setCaptchaImage(`data:image/png;base64,${response.data.image}`);
      setCaptchaValue(''); 
    } catch (error) {
      console.error("Failed to load captcha", error);
      setErrorMsg("Could not load security check.");
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (!userId || !password) { setErrorMsg("Please fill in all fields"); return; }
    if (showCaptcha && !captchaValue) { setErrorMsg("Please enter the captcha code"); return; }

    setTermsChecked(false);
    setCurrentTerms(pickFiveRandom());
    setShowTermsModal(true);
  };

  const onAgree = async () => {
    if (!termsChecked) return;
    setShowTermsModal(false);
    setErrorMsg("");

    const payload = {
        userId: userId, 
        password: password,
        captchaId: showCaptcha ? captchaId : null,
        captchaValue: showCaptcha ? captchaValue : null
    };

    try {
        // 1. CALL API
        const response = await loginUser(payload);
        console.log("Login Success", response.data);
        
        const data = response.data;

        // 2. ✅ SAVE SESSION DATA SECURELY
        // We save individual fields to make them easy to check in other components
        localStorage.setItem("jwtToken", data.jwtToken);
        localStorage.setItem("userId", data.userId);
        localStorage.setItem("role", data.role);
        
        if (data.vendorId) {
            localStorage.setItem("vendorId", data.vendorId);
        }

        // Optional: Keep the full object if you use it elsewhere
        localStorage.setItem("currentUser", JSON.stringify(data));

        // 3. NAVIGATE BASED ON ROLE
        const role = data.role;
        const config = navConfig[role];
        // Default to /overview if role config is missing
        const targetRoute = config?.defaultRoute || "/overview";
        
        navigate(targetRoute);

    } catch (error) {
        if (error.response && error.response.status === 401) {
            const data = error.response.data; 
            setErrorMsg(data.message || "Invalid credentials");
            
            // Handle Captcha Trigger from Backend
            if (data.requiresCaptcha) {
                if (!showCaptcha) { 
                    setShowCaptcha(true); 
                }
                // Always refresh captcha on failure
                fetchCaptcha(); 
                setCaptchaValue(''); 
            }
        } else {
            setErrorMsg(error.response?.data?.message || "Server connection failed.");
        }
    }
  };

  const handleCancel = () => {
    setUserId(""); setPassword(""); setErrorMsg("");
  };

  return (
    <div className="login-body">
      <style>{`
        /* ... Keeping your original CSS ... */
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600;700&family=Space+Mono:wght@700&display=swap');
        :root { --primary-grad: linear-gradient(135deg, #00b09b, #96c93d); --card-grad: linear-gradient(135deg, #11998e, #38ef7d); --dark: #2c3e50; }
        .login-body { background-color: #e0e5ec; background-image: radial-gradient(#d1d8e0 1px, transparent 1px); background-size: 20px 20px; display: flex; justify-content: center; align-items: center; min-height: 100vh; font-family: 'Poppins', sans-serif; margin: 0; padding: 20px; box-sizing: border-box; }
        .main-container { display: flex; width: 900px; max-width: 100%; min-height: 600px; background: #fff; border-radius: 25px; box-shadow: 0 20px 60px rgba(0,0,0,0.15); overflow: hidden; position: relative; }
        .visual-side { flex: 1; background: var(--dark); position: relative; display: flex; justify-content: center; align-items: center; overflow: hidden; padding: 20px; }
        .blob { position: absolute; background: rgba(255,255,255,0.05); border-radius: 50%; }
        .blob-1 { width: 300px; height: 300px; top: -50px; left: -50px; }
        .blob-2 { width: 200px; height: 200px; bottom: -50px; right: -50px; }
        .ticket-wrapper { animation: float 6s ease-in-out infinite; filter: drop-shadow(0 15px 25px rgba(0,0,0,0.3)); max-width: 100%; }
        .ticket { width: 280px; max-width: 100%; background: var(--card-grad); border-radius: 15px; color: white; position: relative; padding: 25px; mask-image: radial-gradient(circle at 0 70%, transparent 15px, black 16px), radial-gradient(circle at 100% 70%, transparent 15px, black 16px); -webkit-mask-image: radial-gradient(circle at 0 70%, transparent 15px, black 16px), radial-gradient(circle at 100% 70%, transparent 15px, black 16px); mask-composite: intersect; }
        .ticket-header { border-bottom: 2px dashed rgba(255,255,255,0.3); padding-bottom: 15px; margin-bottom: 15px; text-align: center; }
        .ticket-price { font-family: 'Space Mono', monospace; font-size: 2.5rem; color: #fff; text-shadow: 2px 2px 0px rgba(0,0,0,0.1); }
        .ticket-label { font-size: 0.8rem; text-transform: uppercase; letter-spacing: 2px; opacity: 0.8; }
        .ticket-body { text-align: center; }
        .qr-code { width: 100px; height: 100px; background: white; padding: 5px; border-radius: 8px; margin: 10px auto 0; display: block; box-shadow: 0 4px 10px rgba(0,0,0,0.1); }
        .form-side { flex: 1.2; padding: 50px; display: flex; flex-direction: column; justify-content: center; background: #fff; }
        .form-header h2 { font-size: 2.2rem; color: var(--dark); margin-bottom: 10px; margin-top: 0; }
        .form-header p { color: #7f8c8d; margin-bottom: 40px; }
        .input-group { position: relative; margin-bottom: 30px; }
        .input-group input { width: 100%; padding: 15px; border: 2px solid #eee; border-radius: 12px; outline: none; font-size: 1rem; background: #f9f9f9; transition: 0.3s; box-sizing: border-box; }
        .input-group label { position: absolute; left: 15px; top: 50%; transform: translateY(-50%); color: #aaa; pointer-events: none; transition: 0.3s; font-size: 0.95rem; background: transparent; }
        .input-group input:focus, .input-group input:not(:placeholder-shown) { border-color: #38ef7d; background: #fff; box-shadow: 0 5px 15px rgba(56, 239, 125, 0.1); }
        .input-group input:focus ~ label, .input-group input:not(:placeholder-shown) ~ label { top: -10px; left: 10px; font-size: 0.8rem; color: #11998e; background: #fff; padding: 0 5px; font-weight: 600; }
        .btn-submit { width: 100%; padding: 16px; background: var(--primary-grad); border: none; border-radius: 12px; color: white; font-size: 1.1rem; font-weight: 600; cursor: pointer; transition: 0.3s; box-shadow: 0 10px 20px rgba(17, 153, 142, 0.3); position: relative; overflow: hidden; }
        .btn-submit:hover { transform: translateY(-3px); box-shadow: 0 15px 25px rgba(17, 153, 142, 0.4); }
        .btn-submit::after { content: ''; position: absolute; top: 0; left: -100%; width: 100%; height: 100%; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent); transition: 0.5s; }
        .btn-submit:hover::after { left: 100%; }
        .btn-cancel { background: #f1f1f1; color: #333; margin-top: 15px; box-shadow: none; }
        .btn-cancel:hover { background: #e1e1e1; box-shadow: none; }
        .extra-links { display: flex; justify-content: flex-end; margin-top: 20px; font-size: 0.9rem; }
        .extra-links a { color: #7f8c8d; text-decoration: none; transition: 0.3s; }
        .extra-links a:hover { color: #11998e; }
        @keyframes float { 0% { transform: translateY(0px) rotate(0deg); } 50% { transform: translateY(-15px) rotate(2deg); } 100% { transform: translateY(0px) rotate(0deg); } }
        @media (max-width: 900px) { .main-container { width: 100%; } }
        @media (max-width: 768px) { .main-container { flex-direction: column; height: auto; min-height: auto; } .visual-side { padding: 40px 20px; min-height: 250px; } .ticket { transform: scale(0.9); } .form-side { padding: 30px 25px; flex: 1; } .form-header h2 { font-size: 1.8rem; } .btn-submit { padding: 14px; } }
      `}</style>

      <div className="main-container">
        <div className="visual-side">
          <div className="blob blob-1"></div>
          <div className="blob blob-2"></div>
          <div className="ticket-wrapper">
            <div className="ticket">
              <div className="ticket-header">
                <div className="ticket-label">SECURE ACCESS</div>
                <div className="ticket-price">VRES</div>
              </div>
              <div className="ticket-body">
                <p style={{ fontSize: "0.85rem", opacity: 0.9, marginBottom: "10px" }}>VOUCHER REDEMPTION & EXECUTION SYSTEM</p>
                <img src={iconqr} alt="System QR Code" className="qr-code" />
              </div>
            </div>
          </div>
        </div>

        <div className="form-side">
          <div className="form-header">
            <h2>Hello!</h2>
            <p>Welcome to the Application.</p>
          </div>
          <form onSubmit={onSubmit} ref={formRef}>
            <div className="input-group">
              <input type="text" value={userId} onChange={(e) => setUserId(e.target.value)} placeholder=" " required />
              <label>User ID</label>
            </div>
            <div className="input-group">
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder=" " required />
              <label>Password</label>
            </div>

            {showCaptcha && (
                <div style={{background: '#f8f9fa', padding: '15px', borderRadius: '12px', marginBottom: '25px', border: '1px solid #e0e0e0'}}>
                    <p style={{margin: '0 0 10px 0', fontSize: '0.9rem', color: '#555'}}>Security Check Required</p>
                    <div style={{display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px'}}>
                        {captchaImage ? <img src={captchaImage} alt="Captcha" style={{border: '1px solid #ccc', borderRadius: '5px', height: '50px'}} /> : <span>Loading...</span>}
                        <button type="button" onClick={fetchCaptcha} style={{background: 'none', border: 'none', color: '#00b09b', fontWeight: 'bold', cursor: 'pointer', fontSize: '1.2rem'}}>↻</button>
                    </div>
                    <input type="text" placeholder="Enter characters from image" value={captchaValue} onChange={(e) => setCaptchaValue(e.target.value)} style={{width: '100%', padding: '10px', border: '2px solid #eee', borderRadius: '8px', outline: 'none', boxSizing: 'border-box'}} />
                </div>
            )}
            
            {errorMsg && <div style={{color: 'red', marginBottom: '15px', fontSize: '0.9rem'}}>{errorMsg}</div>}
            <button type="submit" className="btn-submit">Login</button>
            <button type="button" onClick={handleCancel} className="btn-submit btn-cancel">Cancel</button>
            <div className="extra-links"><Link to="/forgot-password">Forgot Password?</Link></div>
          </form>
        </div>
      </div>

      {showTermsModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={{ marginTop: 0 }}>Terms & Conditions</h3>
            <div style={styles.termsBox}><ol>{currentTerms.map((t, i) => (<li key={i} style={{ marginBottom: 8 }}>{t}</li>))}</ol></div>
            <div style={{ marginTop: 12 }}>
              <input type="checkbox" checked={termsChecked} onChange={(e) => setTermsChecked(e.target.checked)} /> I accept the terms and conditions
            </div>
            <div style={styles.modalActions}>
              <button onClick={() => setShowTermsModal(false)} style={styles.secondaryBtn}>Back</button>
              <button onClick={onAgree} disabled={!termsChecked} style={{ ...styles.primaryBtn, opacity: termsChecked ? 1 : 0.6 }}>Agree</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;

const styles = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000, padding: 20 },
  modal: { background: "#ffffff", padding: 24, borderRadius: 12, width: "100%", maxWidth: 600, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)" },
  termsBox: { maxHeight: 240, overflowY: "auto", border: "1px solid #e5e7eb", padding: 16, marginTop: 16, background: "#f9fafb", borderRadius: 6, fontSize: 14, lineHeight: "1.5" },
  modalActions: { display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 20 },
  primaryBtn: { flex: 1, background: "#00b09b", color: "#ffffff", padding: "12px", border: "none", borderRadius: 6, fontWeight: "600", cursor: "pointer" },
  secondaryBtn: { flex: 1, background: "#f3f4f6", color: "#374151", padding: "12px", border: "1px solid #e5e7eb", borderRadius: 6, fontWeight: "600", cursor: "pointer" }
};